import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import Navigation from '@/components/Frontside/Navigation'
import ProjectHubGantt from '@/components/hub/ProjectHubGantt'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import MoodboardSimple from '@/components/project-detail/MoodboardSimple'
import ParallaxSection from '@/components/project-detail/ParallaxSection'
import ParallaxText from '@/components/project-detail/ParallaxText'
import ProjectImage from '@/components/project-detail/ProjectImage'
import ProjectMindMapSection from '@/components/project-detail/ProjectMindMapSection'

/**
 * Shared rich layout (based on backup ProjectDetail template).
 * `detail` comes from {@link normalizePortfolioProject} or {@link normalizeHubPublicProject}.
 */
export default function ProjectDetailRich({ detail, backTo = { to: '/#projects', label: '← Back to projects' } }) {
  const heroImages = Array.isArray(detail?.heroImages) ? detail.heroImages : []
  const [heroIndex, setHeroIndex] = useState(0)
  const heroRef = useRef(null)
  const [controlsVisible, setControlsVisible] = useState(true)

  useEffect(() => {
    setHeroIndex(0)
  }, [detail?.title])

  useEffect(() => {
    if (!heroImages.length || heroImages.length <= 1) return
    const interval = setInterval(() => {
      setHeroIndex((prev) => (prev === heroImages.length - 1 ? 0 : prev + 1))
    }, 6000)
    return () => clearInterval(interval)
  }, [heroImages.length])

  useEffect(() => {
    const onScroll = () => {
      const el = heroRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      const heroHeight = rect.height || window.innerHeight
      const shouldHide = rect.top < -(0.2 * heroHeight) || rect.bottom < 100
      setControlsVisible(!shouldHide)
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [heroImages.length])

  if (!detail) return null

  const wipImages = detail.wipImages || []
  const keyStatements = detail.keyStatements || []
  const keyElements = detail.keyElements || []
  const moodboardImages = detail.moodboardImages || []
  const mindMap = detail.mindMap || { nodes: [], edges: [] }

  const prevHero = () => setHeroIndex((prev) => (prev === 0 ? heroImages.length - 1 : prev - 1))
  const nextHero = () => setHeroIndex((prev) => (prev === heroImages.length - 1 ? 0 : prev + 1))

  return (
    <div className="min-h-screen bg-background">
      <Navigation currentSection={detail.title || 'Project'} searchValue="" onSearchChange={() => {}} />

      {heroImages.length > 0 && (
        <div className="relative">
          <div className="relative h-screen" ref={heroRef}>
            {heroImages.map((imageUrl, idx) => (
              <div
                key={idx}
                className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                  idx === heroIndex ? 'z-10 opacity-100' : 'pointer-events-none z-0 opacity-0'
                }`}
              >
                <ProjectImage image={imageUrl} projectTitle={detail.title} index={idx} noSpacer />
              </div>
            ))}
            {heroImages.length > 1 && (
              <>
                <button
                  type="button"
                  aria-label="Previous image"
                  onClick={prevHero}
                  className={`absolute left-4 top-1/2 z-30 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur-sm transition-opacity duration-300 ${
                    controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
                  }`}
                >
                  ‹
                </button>
                <button
                  type="button"
                  aria-label="Next image"
                  onClick={nextHero}
                  className={`absolute right-4 top-1/2 z-30 -translate-y-1/2 rounded-full bg-black/40 p-2 text-white backdrop-blur-sm transition-opacity duration-300 ${
                    controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
                  }`}
                >
                  ›
                </button>
                <div
                  className={`absolute bottom-4 left-1/2 z-30 flex -translate-x-1/2 gap-2 transition-opacity duration-300 ${
                    controlsVisible ? 'opacity-100' : 'pointer-events-none opacity-0'
                  }`}
                >
                  {heroImages.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      aria-label={`Image ${i + 1}`}
                      onClick={() => setHeroIndex(i)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        i === heroIndex ? 'w-8 bg-white' : 'w-2 bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <div className="relative bg-background">
        {heroImages.length === 0 && (
          <section className="mx-auto max-w-[900px] px-[5%] pb-8 pt-[120px]">
            <p className="mb-2 text-xs uppercase tracking-wide text-muted-foreground">{detail.category}</p>
            <h1 className="mb-4 text-3xl font-semibold tracking-tight">{detail.title}</h1>
            {detail.excerpt ? (
              <p className="leading-relaxed text-muted-foreground">{detail.excerpt}</p>
            ) : null}
          </section>
        )}

        {detail.vision ? (
          <section className="mx-auto max-w-[900px] px-[5%] pb-16">
            <div className="rounded-2xl border border-border/60 bg-muted/20 px-5 py-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Context</p>
              <p className="whitespace-pre-wrap text-sm leading-relaxed">{detail.vision}</p>
            </div>
          </section>
        ) : null}

        {wipImages.length > 0 && (
          <section className="bg-background px-0 py-[120px]">
            <div className="mx-auto max-w-[1400px] px-[5%]">
              <h2 className="mb-12 text-center text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-tight">
                Work In Progress
              </h2>
              <div className="columns-1 gap-6 sm:columns-2 lg:columns-3 [column-fill:_balance] [&>*]:mb-6">
                {wipImages.map((url, i) => (
                  <div
                    key={i}
                    className="break-inside-avoid overflow-hidden rounded-2xl border border-border/50 bg-background"
                  >
                    <img src={url} alt="" className="h-auto w-full object-contain" loading="lazy" />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {keyStatements.length > 0 && (
          <section className="bg-background px-0 py-[120px]">
            <div className="mx-auto max-w-[1200px] px-[5%]">
              {keyStatements.map((statement, index) => (
                <ParallaxText key={index} index={index}>
                  <h2 className="mb-4 text-[clamp(2rem,4vw,3.5rem)] font-bold tracking-tight">
                    {statement.title || `Statement ${index + 1}`}
                  </h2>
                  <p className="text-[clamp(1.125rem,2vw,1.5rem)] leading-relaxed text-muted-foreground">
                    {statement.content || statement}
                  </p>
                </ParallaxText>
              ))}
            </div>
          </section>
        )}

        {moodboardImages.length > 0 && (
          <section className="bg-secondary px-0 py-[120px]">
            <div className="mx-auto max-w-[1400px] px-[5%]">
              <h2 className="mb-12 text-center text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-tight">Moodboard</h2>
              <MoodboardSimple imageUrls={moodboardImages} />
            </div>
          </section>
        )}

        {keyElements.length > 0 && (
          <section className="bg-background px-0 py-[120px]">
            <div className="mx-auto max-w-[1200px] px-[5%]">
              <h2 className="mb-16 text-center text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-tight">Key Elements</h2>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(300px,1fr))] gap-12">
                {keyElements.map((element, index) => (
                  <ParallaxSection key={index} speed={0.3}>
                    <Card className="shadow-md">
                      <CardHeader>
                        <CardTitle>{element.title || `Element ${index + 1}`}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <CardDescription className="text-base leading-relaxed">
                          {element.description || element}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  </ParallaxSection>
                ))}
              </div>
            </div>
          </section>
        )}

        <ProjectMindMapSection mindMap={mindMap} />

        {detail.modelUrl ? (
          <section className="bg-background px-0 py-[120px]">
            <div className="mx-auto max-w-[1200px] px-[5%]">
              <h2 className="mb-12 text-center text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-tight">3D Model</h2>
              {/* eslint-disable-next-line react/no-unknown-property -- web component */}
              <model-viewer
                src={detail.modelUrl}
                alt="3D model"
                camera-controls
                auto-rotate
                shadow-intensity="1"
                style={{ width: '100%', height: '600px', background: 'transparent' }}
              />
            </div>
          </section>
        ) : null}

        {detail.showTimeline && detail.schedule && Object.keys(detail.schedule).length > 0 ? (
          <section className="bg-secondary px-0 py-[120px]">
            <div className="mx-auto max-w-[1400px] px-[5%]">
              <h2 className="mb-12 text-center text-[clamp(2.5rem,5vw,4rem)] font-bold tracking-tight">Timeline</h2>
              <ProjectHubGantt schedule={detail.schedule} />
            </div>
          </section>
        ) : null}

        <section className="border-t border-border bg-background px-0 py-[120px]">
          <div className="mx-auto max-w-[1200px] px-[5%]">
            <h2 className="mb-8 text-xl font-semibold tracking-tight">Credits</h2>
            <CreditsBlock credits={detail.credits} />
          </div>
        </section>

        <div className="mx-auto max-w-[1200px] px-[5%] pb-16">
          <Link to={backTo.to} className="text-sm text-primary underline-offset-4 hover:underline">
            {backTo.label}
          </Link>
        </div>
      </div>
    </div>
  )
}

function CreditsBlock({ credits }) {
  const c = credits || {}
  const hasClient = typeof c.client_name === 'string' && c.client_name.trim().length > 0
  const hasValidDate = (() => {
    if (!c.project_date) return false
    const d = new Date(c.project_date)
    return !Number.isNaN(d.getTime())
  })()
  const hasParticipants = typeof c.participants === 'string' && c.participants.trim().length > 0

  if (!hasClient && !hasValidDate && !hasParticipants) {
    return <div className="italic text-muted-foreground">Credits information coming soon</div>
  }

  return (
    <div className="space-y-4 text-muted-foreground">
      {hasClient && (
        <div>
          <span className="font-medium text-foreground">Client: </span>
          {c.client_name}
        </div>
      )}
      {hasValidDate && (
        <div>
          <span className="font-medium text-foreground">Date: </span>
          {new Date(c.project_date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          })}
        </div>
      )}
      {hasParticipants && (
        <div>
          <span className="font-medium text-foreground">Participants: </span>
          {c.participants}
        </div>
      )}
    </div>
  )
}
